import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Heart, Droplets, Shield } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatDate } from '../../../../../utils/formatting';

/**
 * Компонент для отображения календаря с фертильными и безопасными днями
 */
const FertilityCalendar = ({ calendarData, results, formData }) => {
  const [displayMonths, setDisplayMonths] = useState([]);
  const [visibleMonthsCount, setVisibleMonthsCount] = useState(2);
  const [startMonthIndex, setStartMonthIndex] = useState(0);

  // Определение отображаемых месяцев на основе данных календаря
  useEffect(() => {
    if (!calendarData || calendarData.length === 0) return;

    // Группировка данных по месяцам
    const monthGroups = {};
    calendarData.forEach(day => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
      
      if (!monthGroups[monthKey]) {
        monthGroups[monthKey] = {
          year: date.getFullYear(),
          month: date.getMonth(),
          days: []
        };
      }
      
      monthGroups[monthKey].days.push(day);
    });

    // Преобразование объекта в массив и сортировка по дате
    const sortedMonths = Object.values(monthGroups).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    setDisplayMonths(sortedMonths);
  }, [calendarData]);

  // Адаптация числа отображаемых месяцев в зависимости от размера экрана
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setVisibleMonthsCount(1);
      } else if (window.innerWidth < 1280) {
        setVisibleMonthsCount(2);
      } else {
        setVisibleMonthsCount(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!calendarData || calendarData.length === 0 || !results) {
    return null;
  }

  // Названия месяцев и дней недели на русском
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  
  const weekdayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Функция для получения стиля дня в зависимости от его типа
  const getDayClass = (dateType) => {
    switch (dateType) {
      case 'ovulation':
        return 'ovulation-day';
      case 'fertile':
        return 'fertile-day';
      case 'peak-fertile':
        return 'peak-fertile-day';
      case 'period':
        return 'period-day';
      case 'safe':
        return 'safe-day';
      default:
        return '';
    }
  };

  // Функция для получения цвета индикатора дня
  const getDayIndicatorColor = (dateType) => {
    switch (dateType) {
      case 'ovulation':
        return 'bg-rose-500';
      case 'fertile':
        return 'bg-rose-300';
      case 'peak-fertile':
        return 'bg-rose-400';
      case 'period':
        return 'bg-purple-400';
      case 'safe':
        return 'bg-green-400';
      default:
        return 'bg-gray-300';
    }
  };
  
  const nextMonth = () => {
    if (startMonthIndex + visibleMonthsCount < displayMonths.length) {
      setStartMonthIndex(startMonthIndex + 1);
    }
  };
  
  const prevMonth = () => {
    if (startMonthIndex > 0) {
      setStartMonthIndex(startMonthIndex - 1);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8 fertility-calendar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <CalendarIcon className="w-5 h-5 mr-2 text-rose-500" />
          Календарь цикла и фертильности
        </h3>

        {/* Кнопки навигации по месяцам */}
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={prevMonth}
            disabled={startMonthIndex === 0}
            aria-label="Предыдущий месяц"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {displayMonths[startMonthIndex] ? 
              `${monthNames[displayMonths[startMonthIndex].month]} ${displayMonths[startMonthIndex].year}` : 
              ''}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={nextMonth}
            disabled={startMonthIndex + visibleMonthsCount >= displayMonths.length}
            aria-label="Следующий месяц"
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Легенда календаря */}
      <div className="flex flex-wrap gap-3 mb-4 justify-center">
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-rose-500 mr-1"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Овуляция</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-rose-300 mr-1"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Фертильные дни</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-purple-400 mr-1"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Менструация</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 rounded-full bg-green-400 mr-1"></div>
          <span className="text-xs text-gray-600 dark:text-gray-400">Безопасные дни</span>
        </div>
      </div>

      {/* Календарная сетка */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayMonths.slice(startMonthIndex, startMonthIndex + visibleMonthsCount).map((monthData, monthIndex) => {
          const { year, month, days } = monthData;
          
          // Получение первого дня месяца
          const firstDayOfMonth = new Date(year, month, 1);
          
          // Определение дня недели для первого дня месяца (0 - воскресенье, 1 - понедельник и т.д.)
          // Корректируем для начала недели с понедельника
          let firstDayWeekday = firstDayOfMonth.getDay(); // 0 = воскресенье
          firstDayWeekday = firstDayWeekday === 0 ? 6 : firstDayWeekday - 1; // 0 = понедельник, 6 = воскресенье
          
          // Количество дней в месяце
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          
          // Создание массива для дней месяца
          const monthDays = [];
          
          // Добавление пустых ячеек для выравнивания календаря
          for (let i = 0; i < firstDayWeekday; i++) {
            monthDays.push(null);
          }
          
          // Добавление всех дней месяца
          for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = date.toISOString().split('T')[0];
            
            // Поиск данных для текущего дня
            const dayData = days.find(d => d.dateStr === dateStr);
            
            monthDays.push({
              day,
              dateStr,
              dateType: dayData ? dayData.dateType : '',
              tooltip: dayData ? dayData.tooltip : ''
            });
          }
          
          return (
            <div key={`${year}-${month}`} className="calendar-month">
              <h4 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-2 text-center">
                {monthNames[month]} {year}
              </h4>
              
              {/* Дни недели */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {weekdayNames.map((weekday, index) => (
                  <div 
                    key={index} 
                    className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center py-1"
                  >
                    {weekday}
                  </div>
                ))}
              </div>
              
              {/* Дни месяца */}
              <div className="grid grid-cols-7 gap-1">
                {monthDays.map((dayInfo, index) => 
                  dayInfo ? (
                    <div
                      key={index}
                      className={`relative h-10 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${
                        getDayClass(dayInfo.dateType) || 'bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                      title={dayInfo.tooltip}
                    >
                      {dayInfo.day}
                      
                      {/* Индикаторы для особых дней */}
                      {dayInfo.dateType && (
                        <span 
                          className={`absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                            getDayIndicatorColor(dayInfo.dateType)
                          }`} 
                          aria-hidden="true"
                        ></span>
                      )}
                    </div>
                  ) : (
                    <div 
                      key={index} 
                      className="h-10"
                    ></div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Подсказки для календаря */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {formData.plannedPregnancy ? (
          <div className="bg-rose-50 dark:bg-rose-900/20 p-3 rounded-lg border border-rose-100 dark:border-rose-800 flex items-start">
            <Heart className="w-5 h-5 text-rose-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-rose-700 dark:text-rose-300 mb-1">Для планирования беременности</h4>
              <p className="text-xs text-rose-600 dark:text-rose-400">
                Наиболее благоприятными для зачатия являются 1-2 дня до овуляции и день овуляции. Яйцеклетка способна к оплодотворению около 24 часов после овуляции.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800 flex items-start">
            <Shield className="w-5 h-5 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Для предохранения</h4>
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Использование только календарного метода не является надежным методом контрацепции. В фертильные дни рекомендуется дополнительная защита.
              </p>
            </div>
          </div>
        )}
        
        <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg border border-purple-100 dark:border-purple-800 flex items-start">
          <Droplets className="w-5 h-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">Точность прогноза</h4>
            <p className="text-xs text-purple-600 dark:text-purple-400">
              Помните, что прогноз основан на средних значениях вашего цикла, а реальная овуляция может наступить раньше или позже. Для повышения точности используйте тесты на овуляцию.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FertilityCalendar;